import { store } from '@/firebase'
import {
  CollectionName,
  FSBookingMailTemplate,
  generateDocId
} from '@WombatTechnology/nitte-time-lib'

import { compact, isUndefined } from 'es-toolkit/compat'
import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentReference,
  getDocs,
  setDoc,
  updateDoc
} from 'firebase/firestore'
import { toData } from './toData'

/**
 * パーソナル予約の予約者向けメールをカスタマイズするテンプレート。
 *
 * Firestoreパス: Teams/{teamId}/BookingMailTemplates/{templateId}
 *
 * Form のパターンをミラーしている。
 */
export class BookingMailTemplate {
  static ID_LENGTH = 8
  static EMPTY_NAME = '名前なし'

  constructor(
    readonly teamId: string,
    readonly data: FSBookingMailTemplate,
    readonly ref: DocumentReference<FSBookingMailTemplate>
  ) {}

  static async builds(teamId: string): Promise<BookingMailTemplate[]> {
    const qs = await getDocs(BookingMailTemplate.collection(teamId))
    const templates = await Promise.all(
      qs.docs.map(async (templateDoc) => {
        return this.build(templateDoc.ref)
      })
    )
    return compact(templates)
  }

  static async build(
    ref: DocumentReference<FSBookingMailTemplate>
  ): Promise<BookingMailTemplate | null> {
    const data = await toData(ref)
    if (isUndefined(data)) {
      return null
    }
    const teamId = ref.parent.parent!.id
    return new BookingMailTemplate(teamId, data, ref)
  }

  static doc(teamId: string, templateId: string) {
    return doc(this.collection(teamId), templateId)
  }

  static collection(teamId: string) {
    return collection(
      store,
      `${CollectionName.Teams}/${teamId}/${CollectionName.BookingMailTemplates}`
    ) as CollectionReference<FSBookingMailTemplate>
  }

  static async create(
    teamId: string,
    templateId: string,
    data: FSBookingMailTemplate
  ) {
    await setDoc(this.doc(teamId, templateId), data)
  }

  static async update(
    teamId: string,
    templateId: string,
    data: Partial<FSBookingMailTemplate>
  ) {
    await updateDoc(this.doc(teamId, templateId), data)
  }

  static async updateField(
    teamId: string,
    templateId: string,
    path: string,
    value: unknown
  ) {
    await updateDoc(this.doc(teamId, templateId), {
      [path]: value
    } as Partial<FSBookingMailTemplate>)
  }

  get id(): string {
    return this.ref.id
  }

  get title(): string {
    return this.data.title
  }

  async delete() {
    await deleteDoc(this.ref)
  }

  async duplicate() {
    const newId = generateDocId(BookingMailTemplate.ID_LENGTH)
    const title = `${this.data.title}のコピー`
    return BookingMailTemplate.create(this.teamId, newId, {
      ...this.data,
      title
    })
  }
}
